<template>
  <h2 id="level-dashboard-heading" class="sr-only">
    {{
      $t('LevelDashboard.sectionHeading', {
        growth: $t('LevelDashboard.growthTitle'),
        level: displayLevel,
      })
    }}
  </h2>
  <div class="flex flex-col">
    <div class="card flex flex-col gap-4 md:gap-5 w-full items-center">
      <!-- Level badge + growth row -->
      <div class="flex flex-row gap-4 md:gap-5 w-full items-center">
        <div class="rounded-xl bg-[var(--color-orange)] p-3 flex flex-col items-center
          min-w-16 md:min-w-20 shrink-0" aria-hidden="true">
          <span class="text-base font-bold uppercase tracking-wider text-white leading-tight">
            {{ $t('LevelDashboard.levelBadgeLabel') }}
          </span>
          <span class="text-3xl md:text-4xl font-bold leading-none text-white mt-0.5">
            {{ displayLevel }}
          </span>
        </div>
        <div class="flex-1 flex flex-col gap-2 min-w-0">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 class="m-0 text-xl sm:text-xl font-bold text-[var(--color-black)]">
              {{ $t('LevelDashboard.growthTitle') }}
            </h3>
            <span class="text-base font-normal text-[var(--color-inactive-dark)] whitespace-nowrap">
              {{ currentXP }} / {{ totalXP }} {{ $t('LevelDashboard.pointsUnit') }}
            </span>
          </div>

          <ProgressBar
            :value="progressPercent"
            :showValue="false"
            class="level-dashboard-progress w-full"
            :pt="{
              root: {
                class: 'h-2.5 rounded-full bg-[var(--color-light-grey)] border-none',
              },
              value: {
                class: 'rounded-full bg-[var(--color-orange)]',
              },
            }"
            :aria-label="
              isMaximumLevelReached
                ? $t('LevelDashboard.maximumLevelReached')
                : $t('LevelDashboard.progressAria', { percent: progressPercent })
            "
          />

          <div class="flex flex-wrap justify-between gap-2 text-base">
            <template v-if="isMaximumLevelReached">
              <span class="text-[var(--color-inactive-dark)]">
                {{ $t('LevelDashboard.maximumLevelReached') }}
              </span>
            </template>
            <template v-else>
              <span class="text-[var(--color-inactive-dark)]">
                {{ $t('LevelDashboard.percentComplete', { percent: progressPercent }) }}
              </span>
              <span class="font-bold text-[var(--color-orange)]">
                {{ $t('LevelDashboard.xpRemaining', { n: xpRemaining }) }}
              </span>
            </template>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-4 md:gap-5 w-full items-center">
        <!-- Ranking summary -->
        <div v-if="showRankings" class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
          <div class="border border-[var(--border-color)] rounded-lg px-4 py-3">
            <p class="text-sm font-semibold uppercase tracking-wider text-[var(--color-inactive-dark)] m-0 mb-1">
              {{ $t('LevelDashboard.orgLabel') }}
            </p>
            <p class="text-4xl font-bold text-[var(--color-orange)] m-0">
              #{{ dashboardPayload.organizationRank }}
            </p>
            <p class="text-base text-[var(--color-inactive-dark)] m-0 mt-1">
              {{ $t('LevelDashboard.rankOfTotal', { total: dashboardPayload.organizationTotal }) }}
            </p>
          </div>
          <div class="border border-[var(--border-color)] rounded-lg px-4 py-3">
            <p class="text-sm font-semibold uppercase tracking-wider text-[var(--color-inactive-dark)] m-0 mb-1">
              {{ $t('LevelDashboard.teamLabel') }}
            </p>
            <p class="text-4xl font-bold text-[var(--color-orange)] m-0">
              #{{ dashboardPayload.teamRank }}
            </p>
            <p class="text-base text-[var(--color-inactive-dark)] m-0 mt-1">
              {{ $t('LevelDashboard.rankOfTeamMembers', { total: dashboardPayload.teamTotal }) }}
            </p>
          </div>
        </div>
        <div
          v-else
          class="mt-6 w-full rounded-lg border border-[var(--border-color)] bg-[var(--color-light-grey)] px-4 py-3"
          role="status"
        >
          <p class="text-base text-[var(--color-black)] m-0">
            {{ $t('LevelDashboard.rankingNotEligibleMessage') }}
          </p>
        </div>
        <div v-if="!isMaximumLevelReached" class="flex w-full flex-row items-center gap-3 text-sm m-0">
          <div
            class="w-1 h-5 shrink-0 rounded-sm bg-[var(--color-orange)]"
            aria-hidden="true"/>
          <p class="m-0 flex-1 min-w-0 text-xl font-medium text-[var(--color-black)]">
            {{
              $t('LevelDashboard.activitySummaryLine', {
                weeklyPoints: formatPoints(dashboardPayload.weeklyXpThisWeek),
                streakWeeks: dashboardPayload.consecutiveActiveWeeks,
              })
            }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="grid grid-rows-1 w-full gap-4 md:gap-5 sm:grid-cols-2"
    >
      <div class="card min-h-0 p-4">
        <template v-if="isMaximumLevelReached">
          <p class="text-xl font-bold tracking-wide text-[var(--color-black)] m-0 mb-2">
            {{ $t('LevelDashboard.maximumLevelReached') }}
          </p>
          <p class="text-base text-[var(--color-black)] m-0 mb-3">
            {{ $t('LevelDashboard.noFurtherLevelAvailable') }}
          </p>
        </template>
        <template v-else-if="isOnHighestConfiguredLevel">
          <p class="text-xl font-bold tracking-wide text-[var(--color-black)] m-0 mb-2">
            {{ $t('LevelDashboard.untilTopLevelGoalsTitle') }}
          </p>
          <p class="text-3xl font-semibold text-[var(--color-orange)] m-0 mb-1">
            {{ $t('LevelDashboard.pendingPoints', { n: xpRemaining }) }}
          </p>
          <p class="text-base text-[var(--color-inactive-dark)] m-0 mb-3">
            {{ $t('LevelDashboard.xpStillRequired') }}
          </p>
          <p class="text-base text-[var(--color-black)] m-0 mb-3">
            {{
              $t('LevelDashboard.tasksSummary', {
                done: tasksCompleted,
                total: tasksTotal,
                open: tasksOpen,
              })
            }}
          </p>
          <div class="flex flex-wrap gap-2" role="list" :aria-label="$t('LevelDashboard.tasksAria')">
            <Button v-for="(goal, idx) in goals" :key="goal.id || idx" type="button"
              :icon="goal.completed ? 'pi pi-check' : undefined" rounded role="listitem"
              :title="goalTitle(goal)" :aria-label="goal.completed ? goalTitle(goal) : $t('LevelDashboard.taskOpen')"
              class="!w-9 !h-9 !min-w-9 shrink-0 !p-0 !border-[var(--color-orange)] !text-[var(--color-orange)] !cursor-auto"
              :class="goal.completed ? '!bg-[var(--color-answer-card)]' : '!bg-transparent'"/>
          </div>
        </template>
        <template v-else>
          <p class="text-xl font-bold tracking-wide text-[var(--color-black)] m-0 mb-2">
            {{ $t('LevelDashboard.untilLevel', { n: nextLevel }) }}
          </p>
          <p class="text-3xl font-semibold text-[var(--color-orange)] m-0 mb-1">
            {{ $t('LevelDashboard.pendingPoints', { n: xpRemaining }) }}
          </p>
          <p class="text-base text-[var(--color-inactive-dark)] m-0 mb-3">
            {{ $t('LevelDashboard.xpStillRequired') }}
          </p>
          <p class="text-base text-[var(--color-black)] m-0 mb-3">
            {{
              $t('LevelDashboard.tasksSummary', {
                done: tasksCompleted,
                total: tasksTotal,
                open: tasksOpen,
              })
            }}
          </p>
          <div class="flex flex-wrap gap-2" role="list" :aria-label="$t('LevelDashboard.tasksAria')">
            <Button v-for="(goal, idx) in goals" :key="goal.id || idx" type="button"
              :icon="goal.completed ? 'pi pi-check' : undefined" rounded role="listitem"
              :title="goalTitle(goal)" :aria-label="goal.completed ? goalTitle(goal) : $t('LevelDashboard.taskOpen')"
              class="!w-9 !h-9 !min-w-9 shrink-0 !p-0 !border-[var(--color-orange)] !text-[var(--color-orange)] !cursor-auto"
              :class="goal.completed ? '!bg-[var(--color-answer-card)]' : '!bg-transparent'"/>
          </div>
        </template>
      </div>
      <div class="card min-w-0 min-h-0 p-4">
        <p class="text-base font-bold tracking-wide text-[var(--color-black)] m-0 mb-3">
          {{ $t('LevelDashboard.companyRankingTitle') }}
        </p>
        <DataTable
          :value="leaderboardDisplay"
          size="small"
          class="level-dashboard-table text-sm !bg-transparent"
          :rowClass="leaderboardRowClass"
          :pt="{
            root: { class: 'border-none' },
            table: { class: 'w-full' },
          }"
        >
          <Column :header="$t('LevelDashboard.colRank')">
            <template #body="{ data }">
              <span class="font-medium">#{{ data.rank }}</span>
            </template>
          </Column>
          <Column :header="$t('LevelDashboard.colParticipant')">
            <template #body="{ data }">
              <div class="flex items-center gap-2 min-w-0">
                <img
                  :src="rankingAvatarSrc(data)"
                  alt=""
                  class="h-8 w-8 shrink-0 rounded-full object-cover"
                  @error="onRankingAvatarError"
                />
                <span class="min-w-0">
                  {{ data.displayName }}
                  <span
                    v-if="data.isCurrentUser"
                    class="ml-1 text-xs font-semibold text-[var(--color-orange)]"
                  >
                    ({{ $t('LevelDashboard.you') }})
                  </span>
                </span>
              </div>
            </template>
          </Column>
          <Column field="level" :header="$t('LevelDashboard.colLevel')" />
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from 'primevue/progressbar';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import defaultProfileImage from '@/assets/defaultProfileImage.js';

export default {
  name: 'LevelDashboardSection',
  components: {
    ProgressBar,
    DataTable,
    Column,
    Button,
  },
  computed: {
    /** False for OMIND admin / trainer viewers once dashboard API has loaded */
    showRankings() {
      const d = this.$store.getters.getLevelDashboard;
      if (!d) return true;
      return d.rankingEligible !== false;
    },
    /** Dashboard metrics from GET user-level-progress/dashboard; ranks show placeholder when not loaded */
    dashboardPayload() {
      const d = this.$store.getters.getLevelDashboard;
      const missing = '—';
      return {
        weeklyXpThisWeek: d?.weeklyXpThisWeek ?? 0,
        consecutiveActiveWeeks: d?.consecutiveActiveWeeks ?? 0,
        organizationRank: d != null && d.organizationRank != null ? d.organizationRank : missing,
        organizationTotal: d?.organizationTotal ?? 0,
        teamRank: d != null && d.teamRank != null ? d.teamRank : missing,
        teamTotal: d?.teamTotal ?? 0,
      };
    },
    totalXP() {
      return this.$store.getters.getTotalXP;
    },
    currentXP() {
      return this.$store.getters.getCurrentXP;
    },
    displayLevel() {
      return this.$store.getters.getUserLevel;
    },
    nextLevel() {
      return this.displayLevel + 1;
    },
    xpRemaining() {
      const r = this.totalXP - this.currentXP;
      return Math.max(0, r);
    },
    progressPercent() {
      if (!this.totalXP || this.totalXP <= 0) return 0;
      return Math.min(100, Math.round((this.currentXP / this.totalXP) * 100));
    },
    /** All configured levels finished: progress overview has currentLevel >= totalLevels and bar is full. */
    isMaximumLevelReached() {
      const o = this.$store.getters.getLevelProgressOverview;
      if (!o || typeof o.totalLevels !== 'number' || o.totalLevels <= 0) {
        return false;
      }
      return (
        o.currentLevel >= o.totalLevels
        && this.totalXP > 0
        && this.xpRemaining === 0
        && this.progressPercent >= 100
      );
    },
    /** Last configured level index but goals / XP may remain — do not show “until level n+1”. */
    isOnHighestConfiguredLevel() {
      const o = this.$store.getters.getLevelProgressOverview;
      if (!o || typeof o.totalLevels !== 'number' || o.totalLevels <= 0) return false;
      const cur = Number(this.displayLevel);
      if (!Number.isFinite(cur)) return false;
      return cur >= o.totalLevels;
    },
    goals() {
      return this.$store.getters.getLevelInfo || [];
    },
    tasksTotal() {
      return this.goals.length;
    },
    tasksCompleted() {
      return this.goals.filter((g) => g.completed).length;
    },
    tasksOpen() {
      return Math.max(0, this.tasksTotal - this.tasksCompleted);
    },
    /** Company table: only rows from API (never mock); empty until fetchLevelDashboard succeeds */
    leaderboardDisplay() {
      const raw = this.$store.getters.getLevelDashboard?.leaderboardRows;
      if (!Array.isArray(raw) || raw.length === 0) return [];
      const user = this.$store.getters.getUser;
      const fallbackName = user?.name || user?.email || this.$t('LevelDashboard.you');
      return raw.map((row) => ({
        ...row,
        displayName: row.isCurrentUser ? (row.name || fallbackName) : row.name,
        profilePictureSmall: row.profilePictureSmall ?? null,
      }));
    },
  },
  methods: {
    leaderboardRowClass(row) {
      return row.isCurrentUser ? 'level-dashboard-table__row--current' : undefined;
    },
    rankingAvatarSrc(row) {
      const profilePic = row.profilePictureSmall;
      if (profilePic) return profilePic;
      if (row.isCurrentUser) {
        const small = this.$store.getters.getUser?.picture_profile?.small;
        if (small) return small;
      }
      return defaultProfileImage;
    },
    onRankingAvatarError(event) {
      const el = event?.target;
      if (el && el instanceof HTMLImageElement) {
        el.src = defaultProfileImage;
      }
    },
    formatXp(n) {
      const v = Number(n);
      if (!Number.isFinite(v)) return '0';
      return Math.round(v).toLocaleString(this.$i18n?.locale || 'en');
    },
    formatPoints(n) {
      return this.formatXp(n);
    },
    goalTitle(goal) {
      const t = goal?.title;
      if (typeof t === 'string') return t;
      const loc = this.$i18n?.locale || 'en';
      if (t && typeof t === 'object') {
        return t[loc] || t.en || t.de || '';
      }
      return '';
    },
  },
};
</script>

<style scoped>
/*
 * Match .card surface (#f4f4f4); PrimeVue defaults table/header cells to white.
 * Only the current-user row uses --color-answer-card.
 */
:deep(.level-dashboard-table .p-datatable-thead > tr > th) {
  background-color: #f4f4f4 !important;
}

:deep(.level-dashboard-table .p-datatable-tbody > tr > td) {
  background-color: #f4f4f4 !important;
}

:deep(.level-dashboard-table .p-datatable-tbody > tr.level-dashboard-table__row--current),
:deep(.level-dashboard-table .p-datatable-tbody > tr.level-dashboard-table__row--current > td) {
  background-color: var(--color-answer-card) !important;
}
</style>
